/**
 * Website chrome: menu separators, menu search overlay, floating FABs styling.
 * Lives in storefront.moduleSettings.siteChrome (exported with storefront JSON).
 */

import type { CSSProperties } from 'react';

export type StorefrontSiteChromeSettings = {
  /** Thin vertical rules between header menu items. */
  menuSeparators: boolean;
  /** Empty = theme secondary text at ~35% opacity. */
  menuSeparatorColor: string;
  menuSeparatorWidthPx: number;
  /** Magnifier after the last menu item. */
  menuSearchEnabled: boolean;
  /** Empty = header / text primary. */
  searchIconColor: string;
  /** Overlay panel vertical position from viewport top (8–60). */
  searchOverlayTopPercent: number;
  searchOverlayMaxWidthRem: number;
  /** Empty = black 72% veil. */
  searchOverlayVeil: string;
  /** Empty = bg-primary / text-primary / border-color theme tokens. */
  searchOverlayPanelBg: string;
  searchOverlayPanelFg: string;
  searchOverlayFieldBg: string;
  searchOverlayFieldFg: string;
  searchOverlayBorder: string;
  /** Shared ring for cart / fav / back-to-top FABs. */
  fabBorderColor: string;
  fabBorderWidthPx: number;
  /** Empty = bg-primary / text-primary. */
  fabBg: string;
  fabFg: string;
  /** Empty = accent-primary / white. */
  a11yFabBg: string;
  a11yFabFg: string;
  /** Empty = same as fab border. */
  a11yFabBorderColor: string;
  /**
   * Padding on all sides (px) for `#website-footer-inner` — same idea as catalog `insetPx`.
   */
  footerInsetPx: number;
  /**
   * Mobile header only: gap from viewport edge to logo / hamburger (0 = flush).
   */
  headerEdgeInsetPx: number;
};

export const DEFAULT_STOREFRONT_SITE_CHROME: StorefrontSiteChromeSettings = {
  menuSeparators: true,
  menuSeparatorColor: '',
  menuSeparatorWidthPx: 1,
  menuSearchEnabled: true,
  searchIconColor: '',
  searchOverlayTopPercent: 14,
  searchOverlayMaxWidthRem: 42,
  searchOverlayVeil: '',
  searchOverlayPanelBg: '',
  searchOverlayPanelFg: '',
  searchOverlayFieldBg: '',
  searchOverlayFieldFg: '',
  searchOverlayBorder: '',
  fabBorderColor: '#ffffff',
  fabBorderWidthPx: 1,
  fabBg: '',
  fabFg: '',
  a11yFabBg: '',
  a11yFabFg: '#ffffff',
  a11yFabBorderColor: '',
  footerInsetPx: 0,
  headerEdgeInsetPx: 8,
};

function asRecord(raw: unknown): Record<string, unknown> {
  return raw && typeof raw === 'object' && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {};
}

function asBool(raw: unknown, fallback: boolean): boolean {
  if (raw === true || raw === 'true' || raw === 1 || raw === '1') return true;
  if (raw === false || raw === 'false' || raw === 0 || raw === '0') return false;
  return fallback;
}

function asString(raw: unknown, fallback = ''): string {
  if (typeof raw !== 'string') return fallback;
  return raw.trim();
}

function clampInt(raw: unknown, min: number, max: number, fallback: number): number {
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
}

function clampFloat(raw: unknown, min: number, max: number, fallback: number): number {
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

/** Accept #rgb / #rrggbb / #rrggbbaa or rgba()/hsla() loosely; else empty. */
export function normalizeChromeColor(raw: unknown, fallback = ''): string {
  const s = asString(raw);
  if (!s) return fallback;
  if (/^#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(s)) {
    return s.toLowerCase();
  }
  if (/^(rgb|rgba|hsl|hsla)\(/i.test(s)) return s;
  return fallback;
}

export function normalizeStorefrontSiteChrome(raw: unknown): StorefrontSiteChromeSettings {
  const source = asRecord(raw);
  const d = DEFAULT_STOREFRONT_SITE_CHROME;
  return {
    menuSeparators: asBool(source.menuSeparators ?? source.menu_separators, d.menuSeparators),
    menuSeparatorColor: normalizeChromeColor(
      source.menuSeparatorColor ?? source.menu_separator_color,
      d.menuSeparatorColor,
    ),
    menuSeparatorWidthPx: clampInt(
      source.menuSeparatorWidthPx ?? source.menu_separator_width_px,
      1,
      4,
      d.menuSeparatorWidthPx,
    ),
    menuSearchEnabled: asBool(
      source.menuSearchEnabled ?? source.menu_search_enabled,
      d.menuSearchEnabled,
    ),
    searchIconColor: normalizeChromeColor(
      source.searchIconColor ?? source.search_icon_color,
      d.searchIconColor,
    ),
    searchOverlayTopPercent: clampInt(
      source.searchOverlayTopPercent ?? source.search_overlay_top_percent,
      8,
      60,
      d.searchOverlayTopPercent,
    ),
    searchOverlayMaxWidthRem: clampFloat(
      source.searchOverlayMaxWidthRem ?? source.search_overlay_max_width_rem,
      20,
      64,
      d.searchOverlayMaxWidthRem,
    ),
    searchOverlayVeil: normalizeChromeColor(
      source.searchOverlayVeil ?? source.search_overlay_veil,
      d.searchOverlayVeil,
    ),
    searchOverlayPanelBg: normalizeChromeColor(
      source.searchOverlayPanelBg ?? source.search_overlay_panel_bg,
      d.searchOverlayPanelBg,
    ),
    searchOverlayPanelFg: normalizeChromeColor(
      source.searchOverlayPanelFg ?? source.search_overlay_panel_fg,
      d.searchOverlayPanelFg,
    ),
    searchOverlayFieldBg: normalizeChromeColor(
      source.searchOverlayFieldBg ?? source.search_overlay_field_bg,
      d.searchOverlayFieldBg,
    ),
    searchOverlayFieldFg: normalizeChromeColor(
      source.searchOverlayFieldFg ?? source.search_overlay_field_fg,
      d.searchOverlayFieldFg,
    ),
    searchOverlayBorder: normalizeChromeColor(
      source.searchOverlayBorder ?? source.search_overlay_border,
      d.searchOverlayBorder,
    ),
    fabBorderColor: normalizeChromeColor(
      source.fabBorderColor ?? source.fab_border_color,
      d.fabBorderColor,
    ) || d.fabBorderColor,
    fabBorderWidthPx: clampInt(
      source.fabBorderWidthPx ?? source.fab_border_width_px,
      0,
      4,
      d.fabBorderWidthPx,
    ),
    fabBg: normalizeChromeColor(source.fabBg ?? source.fab_bg, d.fabBg),
    fabFg: normalizeChromeColor(source.fabFg ?? source.fab_fg, d.fabFg),
    a11yFabBg: normalizeChromeColor(source.a11yFabBg ?? source.a11y_fab_bg, d.a11yFabBg),
    a11yFabFg: normalizeChromeColor(source.a11yFabFg ?? source.a11y_fab_fg, d.a11yFabFg)
      || d.a11yFabFg,
    a11yFabBorderColor: normalizeChromeColor(
      source.a11yFabBorderColor ?? source.a11y_fab_border_color,
      d.a11yFabBorderColor,
    ),
    footerInsetPx: clampInt(
      source.footerInsetPx ?? source.footer_inset_px,
      0,
      64,
      d.footerInsetPx,
    ),
    headerEdgeInsetPx: clampInt(
      source.headerEdgeInsetPx ?? source.header_edge_inset_px,
      0,
      32,
      d.headerEdgeInsetPx,
    ),
  };
}

/** CSS custom properties on the website shell (and dialog portal). */
export function applyStorefrontSiteChromeCss(
  el: HTMLElement | null,
  chrome: StorefrontSiteChromeSettings,
): void {
  if (!el) return;
  const set = (key: string, value: string) => {
    if (value) el.style.setProperty(key, value);
    else el.style.removeProperty(key);
  };

  set('--sf-chrome-menu-sep', chrome.menuSeparatorColor);
  el.style.setProperty('--sf-chrome-menu-sep-w', `${chrome.menuSeparatorWidthPx}px`);
  set('--sf-chrome-search-icon', chrome.searchIconColor);

  el.style.setProperty('--sf-chrome-search-top', `${chrome.searchOverlayTopPercent}%`);
  el.style.setProperty('--sf-chrome-search-max-w', `${chrome.searchOverlayMaxWidthRem}rem`);
  set(
    '--sf-chrome-search-veil',
    chrome.searchOverlayVeil || 'rgb(0 0 0 / 0.72)',
  );
  set('--sf-chrome-search-panel-bg', chrome.searchOverlayPanelBg || 'var(--bg-primary)');
  set('--sf-chrome-search-panel-fg', chrome.searchOverlayPanelFg || 'var(--text-primary)');
  set('--sf-chrome-search-field-bg', chrome.searchOverlayFieldBg || 'var(--bg-secondary, var(--bg-primary))');
  set('--sf-chrome-search-field-fg', chrome.searchOverlayFieldFg || 'var(--text-primary)');
  set('--sf-chrome-search-border', chrome.searchOverlayBorder || 'var(--border-color)');

  el.style.setProperty('--sf-chrome-fab-border-w', `${chrome.fabBorderWidthPx}px`);
  set('--sf-chrome-fab-border', chrome.fabBorderColor || '#ffffff');
  set('--sf-chrome-fab-bg', chrome.fabBg || 'var(--bg-primary)');
  set('--sf-chrome-fab-fg', chrome.fabFg || 'var(--text-primary)');
  set('--sf-chrome-a11y-bg', chrome.a11yFabBg || 'var(--accent-primary)');
  set('--sf-chrome-a11y-fg', chrome.a11yFabFg || '#ffffff');
  set(
    '--sf-chrome-a11y-border',
    chrome.a11yFabBorderColor || chrome.fabBorderColor || '#ffffff',
  );
}

export function siteChromeFabStyle(
  chrome: StorefrontSiteChromeSettings,
): CSSProperties {
  return {
    borderWidth: chrome.fabBorderWidthPx,
    borderStyle: chrome.fabBorderWidthPx > 0 ? 'solid' : 'none',
    borderColor: chrome.fabBorderColor || '#ffffff',
    background: chrome.fabBg || 'var(--bg-primary)',
    color: chrome.fabFg || 'var(--text-primary)',
  };
}
